import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../services/AppContext/appProvider";
import {
  checkValidPage,
  getCurrentPageQuestion,
  saveQuestionEngagementLocalStorage
} from "../../utilities/aiChat";
import {
  AI_CHAT_NOTIFICATION_DELAY,
  AI_NOTIFICATION_DISPLAY_TIME
} from "../../utilities/constants";

export const AiChatNotification: React.FC = () => {
  const { t } = useTranslation();
  const {
    showChatNotificationModal,
    setShowChatNotificationModal,
    setShowChatBox,
    currentPage,
    setCurrentPage,
    aiMessages,
    setAiMessages,
    aiSystemMessage,
    setAiSystemMessage,
    notificationPositionTop,
    timeoutRef,
    hideNotificationTimeOutRef
  } = useContext(UserContext);

  // // Track page changes
  // Controller - sets the notification model message based on current page
  // !NOTE: Page tracking is handled by the aiChat.component
  useEffect(() => {
    // Check if the current page is in the list of pages to fetch messages
    const isValidPage = checkValidPage(currentPage);
    if (isValidPage) {
      // attempt to retrieve a question for this page
      const currentQuestion = getCurrentPageQuestion(currentPage);
      if (currentQuestion) {
        setAiSystemMessage(currentQuestion);
        if (document.readyState === "complete") {
          if (timeoutRef) {
            clearAllTimers();
            timeoutRef.current = window.setTimeout(() => {
              setShowChatNotificationModal(true);
            }, AI_CHAT_NOTIFICATION_DELAY);
          }
        } else {
          const onLoad = () => {
            clearAllTimers();
            timeoutRef.current = window.setTimeout(() => {
              setShowChatNotificationModal(true);
            }, AI_CHAT_NOTIFICATION_DELAY);
          };
          window.addEventListener("load", onLoad, { once: true });

          return () => window.removeEventListener("load", onLoad);
        }
      } else {
        clearAllTimers();
        setAiSystemMessage(null);
        setShowChatNotificationModal(false);
      }
    }
    // Clear timeout if component unmounts
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [currentPage]);

  // ------------------------------------------------------------------------------
  // TIMERS
  // Clear timers helper
  function clearAllTimers() {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (hideNotificationTimeOutRef.current) {
      clearTimeout(hideNotificationTimeOutRef.current);
      hideNotificationTimeOutRef.current = null;
    }
  }

  // When popup shows, start timer to auto-hide after AI_NOTIFICATION_DISPLAY_TIME
  useEffect(() => {
    if (showChatNotificationModal) {
      if (hideNotificationTimeOutRef.current) {
        clearTimeout(hideNotificationTimeOutRef.current);
      }
      hideNotificationTimeOutRef.current = window.setTimeout(() => {
        setShowChatNotificationModal(false);
      }, AI_NOTIFICATION_DISPLAY_TIME);
    } else {
      // If popup hidden, clear auto-hide timer
      if (hideNotificationTimeOutRef.current) {
        clearTimeout(hideNotificationTimeOutRef.current);
        hideNotificationTimeOutRef.current = null;
      }
    }
  }, [showChatNotificationModal]);

  // handle notification modal close
  const handleNotificationClose = () => {
    if (aiSystemMessage && aiSystemMessage.trim() !== "") {
      saveQuestionEngagementLocalStorage(currentPage, aiSystemMessage);
    }
    clearAllTimers();
    setShowChatNotificationModal(false);
    setAiSystemMessage(null);
    setAiMessages(null);
  };

  const handleNotificationClick = () => {
    // click on show chat box means question engagement
    if (aiSystemMessage && aiSystemMessage.trim() !== "") {
      saveQuestionEngagementLocalStorage(currentPage, aiSystemMessage);
      setAiMessages([
        {
          system: true,
          ai: true,
          content: t(aiSystemMessage),
          time: Math.floor(Date.now() / 1000) // Set time as epoch
        }
      ]);
    }

    setShowChatNotificationModal(false);
    setShowChatBox(true);
  };

  return showChatNotificationModal && aiSystemMessage ? (
    <div
      className={`z-100 fixed right-4 flex max-w-[80%] items-center rounded-lg bg-secondary p-1 md:max-w-[40%] lg:max-w-[25%] ${notificationPositionTop ? "top-2 lg:top-20 [@media_(min-width:1024px)_and_(max-width:1150px)]:top-2" : "top-20"}`}
      style={{ zIndex: 1000 }}
    >
      {/* Close Button */}
      <button
        className="absolute right-2 top-0 text-2xl font-bold text-base-100 hover:text-secondary"
        onClick={handleNotificationClose}
      >
        &times;
      </button>
      <div
        className="mr-4 flex-grow cursor-pointer font-bold text-white"
        role="alert"
      >
        {/* Top Section - Image and Title */}
        <div className="border-b-chat-color flex flex-row items-center justify-start gap-2 border-b-2 pb-1">
          <img
            className="h-10 w-10 rounded-full"
            src="/assets/images/Jayda_ai.webp"
            alt="ai_chat_image"
          />
          <h1
            className={`text-md cursor-pointer font-bold text-white md:text-lg`}
          >
            {t("AI Chat Heading")}
          </h1>
        </div>
        {/* Message Content */}
        <div className="p-2">
          <p className="text-sm">{t(aiSystemMessage)}</p>
        </div>

        {/* Open Chat Block Button */}
        <div className="flex items-center justify-center">
          <button
            className="btn btn-outline btn-sm mt-2 rounded-lg text-sm font-bold text-white hover:border-white hover:bg-base-100 hover:text-secondary hover:outline-white"
            onClick={handleNotificationClick}
          >
            {t("chat-reply-button")}
          </button>
        </div>
      </div>
    </div>
  ) : null;
};
